/**
 * M5T Chat Waiting - the bubble that occupies a silent pass
 *
 * The routing pass does not stream, so between a prompt and its answer there is
 * a stretch of nothing at all. Dead air is what reads as broken - two seconds
 * with something on screen feels responsive, two seconds of blank canvas feels
 * like the send failed and invites a retype.
 *
 * Rather than a skeleton of the eventual answer (which would be a lie, since we
 * do not yet know whether the answer is a view or prose), this rotates short
 * phrases describing what the app can do. The wait teaches the surface the user
 * is waiting on - discovery is otherwise the hard problem with a conversational
 * UI, because nothing on screen advertises what it will understand.
 *
 *   const el = document.createElement('m5t-chat-waiting');
 *   el.phrases = capabilityTips();   // from the template registry
 *
 * Phrases are a property, not an attribute - they are an array, and the caller
 * already has it in hand. A `phrases` attribute holding JSON is accepted too so
 * the element is usable from static markup.
 *
 * The element does not decide when it disappears. Whoever showed it removes it,
 * which keeps a stalled or failed pass from leaving an animation running
 * forever with nothing behind it.
 */

const DEFAULT_INTERVAL = 3200;

const FALLBACK = ['Working…'];

/**
 * Where the next instance starts in the list. Consecutive waits would otherwise
 * always open on the same phrase, which reads as a static string rather than a
 * rotation - and makes the second wait feel like the first one never ended.
 */
let startOffset = 0;

const STYLES = `
  :host {
    display: flex;
    flex-direction: column;
    align-self: flex-start;
    max-width: 70%;
    animation: slideIn 0.3s ease-out;
  }

  @keyframes slideIn {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .bubble-content {
    background: #2d2d2d;
    color: #a8a8a8;
    border-radius: 18px 18px 18px 4px;
    border: 1px solid #3a3a3a;
    padding: 0.875rem 1.25rem;
    font-size: 0.95rem;
    line-height: 1.5;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-block-size: 1.5em;
  }

  /* Activity, so the rotation reads as work in progress rather than a slideshow */
  .dots {
    display: inline-flex;
    gap: 4px;
    flex: none;
  }

  .dots i {
    inline-size: 6px;
    block-size: 6px;
    border-radius: 50%;
    background: #edba08;
    animation: blink 1.4s ease-in-out infinite;
  }

  .dots i:nth-child(2) { animation-delay: 0.2s; }
  .dots i:nth-child(3) { animation-delay: 0.4s; }

  @keyframes blink {
    0%, 80%, 100% { opacity: 0.2; }
    40%           { opacity: 1; }
  }

  .phrase {
    transition: opacity 0.35s ease;
    text-wrap: pretty;
  }

  .phrase[data-fading="true"] { opacity: 0; }

  @media (prefers-reduced-motion: reduce) {
    :host { animation: none; }
    .dots i { animation: none; opacity: 0.65; }
    .phrase { transition: none; }
  }
`;

class M5TChatWaiting extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this._phrases = null;
    this._index = 0;
    this._timer = null;
    this._built = false;
  }

  static get observedAttributes() {
    return ['phrases', 'interval'];
  }

  set phrases(list) {
    this._phrases = Array.isArray(list) ? list.filter(Boolean) : null;
    this._index = 0;
    if (this._built) this.paint();
  }

  get phrases() {
    if (this._phrases?.length) return this._phrases;

    const attr = this.getAttribute('phrases');
    if (attr) {
      try {
        const parsed = JSON.parse(attr);
        if (Array.isArray(parsed) && parsed.length) return parsed.filter(Boolean);
      } catch {
        // A malformed attribute must not blank the bubble - fall through.
      }
    }

    return FALLBACK;
  }

  get interval() {
    const n = Number(this.getAttribute('interval'));
    return Number.isFinite(n) && n > 0 ? n : DEFAULT_INTERVAL;
  }

  connectedCallback() {
    this.build();

    // Stagger where this instance opens, then advance the shared cursor.
    const list = this.phrases;
    this._index = list.length ? startOffset % list.length : 0;
    startOffset = (startOffset + 1) % Math.max(list.length, 1);

    this.paint();
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  attributeChangedCallback(name) {
    if (!this._built) return;
    if (name === 'phrases') this.paint();
    if (name === 'interval') this.start();
  }

  build() {
    if (this._built) return;

    this.shadowRoot.innerHTML = `
      <style>${STYLES}</style>
      <div class="bubble-content">
        <span class="dots" aria-hidden="true"><i></i><i></i><i></i></span>
        <span class="phrase"></span>
      </div>
    `;

    this._phraseEl = this.shadowRoot.querySelector('.phrase');
    this._built = true;

    // Announced politely: the phrases are ambient, not something to interrupt for.
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');
  }

  start() {
    this.stop();
    if (this.phrases.length < 2) return; // nothing to rotate to
    this._timer = setInterval(() => this.advance(), this.interval);
  }

  stop() {
    if (this._timer) clearInterval(this._timer);
    this._timer = null;
  }

  advance() {
    const list = this.phrases;
    this._index = (this._index + 1) % list.length;

    // Fade out, swap, fade in. Guarded on isConnected because the swap lands a
    // frame later and the bubble is routinely removed mid-transition.
    this._phraseEl.dataset.fading = 'true';

    setTimeout(() => {
      if (!this.isConnected) return;
      this.paint();
      this._phraseEl.dataset.fading = 'false';
    }, 350);
  }

  paint() {
    if (!this._phraseEl) return;
    const list = this.phrases;
    this._phraseEl.textContent = list[this._index % list.length] ?? FALLBACK[0];
  }
}

customElements.define('m5t-chat-waiting', M5TChatWaiting);

export default M5TChatWaiting;
